import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';

const EventsTab = ({ token, dateRange }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', module: '', search: '', userId: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);

  // Charger la liste des utilisateurs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/users`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();
        if (result.success) {
          setUsers(result.data || []);
        }
      } catch (err) {
        console.error('Erreur chargement utilisateurs:', err);
      }
    };
    fetchUsers();
  }, [token]);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          startDate: dateRange.start,
          endDate: dateRange.end,
          page: page.toString(),
          limit: '50',
          ...(filters.category && { category: filters.category }),
          ...(filters.module && { module: filters.module }),
          ...(filters.search && { search: filters.search }),
          ...(filters.userId && { userId: filters.userId })
        });

        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/analytics/dashboard/events?${params}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();

        if (result.success && result.data.available) {
          setEvents(result.data.events);
          setTotalPages(result.data.totalPages);
        }
      } catch (err) {
        console.error('Erreur chargement events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [token, dateRange, page, filters]);

  const handleExport = async (format) => {
    const params = new URLSearchParams({
      format,
      type: 'events',
      startDate: dateRange.start,
      endDate: dateRange.end
    });

    window.open(
      `${API_ENDPOINTS.BASE_URL}/api/analytics/dashboard/export?${params}`,
      '_blank'
    );
  };

  return (
    <div>
      {/* Filtres */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            minWidth: '200px'
          }}
        />
        <select
          value={filters.category}
          onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px' }}
        >
          <option value="">Toutes categories</option>
          <option value="analytics">Analytics</option>
          <option value="audit">Audit</option>
          <option value="presence">Presence</option>
          <option value="perf">Performance</option>
        </select>
        <select
          value={filters.module}
          onChange={(e) => setFilters(f => ({ ...f, module: e.target.value }))}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px' }}
        >
          <option value="">Tous modules</option>
          <option value="planning">Planning</option>
          <option value="users">Utilisateurs</option>
          <option value="groups">Groupes</option>
          <option value="clients">Clients</option>
          <option value="hr">RH</option>
        </select>
        <select
          value={filters.userId}
          onChange={(e) => setFilters(f => ({ ...f, userId: e.target.value }))}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', minWidth: '180px' }}
        >
          <option value="">Tous utilisateurs</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button
            onClick={() => handleExport('csv')}
            style={{
              padding: '8px 16px',
              background: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            style={{
              padding: '8px 16px',
              background: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Utilisateur</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Categorie</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Evenement</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Module</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {new Date(event.created_at).toLocaleString('fr-FR')}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>
                    {event.user_name || '-'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500',
                      background: {
                        analytics: '#e3f2fd',
                        audit: '#fff3e0',
                        presence: '#e8f5e9',
                        perf: '#fce4ec'
                      }[event.category] || '#f5f5f5',
                      color: {
                        analytics: '#1976d2',
                        audit: '#f57c00',
                        presence: '#388e3c',
                        perf: '#c2185b'
                      }[event.category] || '#666'
                    }}>
                      {event.category}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>{event.event_name}</td>
                  <td style={{ padding: '12px', fontSize: '13px' }}>{event.module || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          padding: '16px',
          borderTop: '1px solid #eee'
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.5 : 1,
              background: 'white'
            }}
          >
            Precedent
          </button>
          <span style={{ padding: '8px 16px', color: '#666' }}>
            Page {page} / {totalPages || 1}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: page === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer',
              opacity: page === totalPages || totalPages === 0 ? 0.5 : 1,
              background: 'white'
            }}
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventsTab;
